import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { Router, ActivatedRoute } from '@angular/router';
import { BreadcrumbService } from '@k12/navigation';
import { AppInsightsService } from '@k12/error_handler';
import { AdminUserType, ProviderData } from '@k12/models';
import { IUserRecordProviderDetailsService } from '@k12/tokens';
import { WORK_QUEUE_URL } from '@k12/admin-components';
import { Subject, takeUntil } from 'rxjs';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,

  selector: 'lib-user-record-view-task',
  imports: [MatButtonModule, MatIconModule, MatProgressBarModule],
  templateUrl: './user-record-view-task.component.html',
  styleUrls: ['./user-record-view-task.component.scss']
})
export class UserRecordViewTaskComponent implements OnInit, OnDestroy {
  // Input for services that need to be injected from the consuming application
  @Input() userRecordService!: IUserRecordProviderDetailsService;

  @Output() formSubmit = new EventEmitter<boolean>();

  personaId: number | null = null;
  userId: string | null = null;
  userData: ProviderData | null = null;
  accountId: string | null = null;
  private routeUnsubscribe = new Subject<void>();
  private readonly appInsightsService = inject(AppInsightsService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly breadcrumbService = inject(BreadcrumbService);

  ngOnInit(): void {
    // Get account ID from route parameters
    this.route.params.pipe(takeUntil(this.routeUnsubscribe)).subscribe((params) => {
      this.accountId = params['id'] || null;
      this.userId = this.accountId;
    });

    if (history.state && history.state['personaId']) {
      this.personaId = history.state['personaId'];
    }

    if (history.state && history.state['providerId']) {
      this.userId = history.state['providerId'];
      this.accountId = this.userId;
    }
    this.getUserDataForBreadcrumbs();

    document.documentElement.scrollTop = 0;
  }

  ngOnDestroy(): void {
    this.routeUnsubscribe.next();
    this.routeUnsubscribe.complete();
    this.breadcrumbService.clearDynamicBreadcrumbs();
  }

  // Method to get user data and set breadcrumbs
  private getUserDataForBreadcrumbs(): void {
    const userId = this.userId;
    if (userId && this.userRecordService) {
      this.userRecordService.getProviderDetails(userId, AdminUserType.Provider).subscribe({
        next: (res: unknown) => {
          this.userData = res as ProviderData;
        },
        error: (error: unknown) => this.appInsightsService.log(error instanceof Error ? error : new Error(String(error)))
      });
    }
    this.setBreadcrumbsForViewTask();
  }

  // Method to set breadcrumbs for the view task page
  private setBreadcrumbsForViewTask(): void {
    if (this.userData && this.userData.firstName && this.userData.lastName && this.userId) {
      const userName = `${this.userData.firstName} ${this.userData.lastName}`;
      // Set breadcrumbs for user record and view task pages
      this.breadcrumbService.setNestedDynamicBreadcrumbs(WORK_QUEUE_URL, this.userId, userName, undefined, 'View Task');
    } else if (this.userRecordService) {
      const routeSegments = this.router.url.split('/');
      const userId = routeSegments[2];
      this.userRecordService.getProviderDetails(userId, AdminUserType.Provider).subscribe({
        next: (res: unknown) => {
          const data = res as ProviderData | null;
          if (data) {
            if (data.businessName) {
              this.breadcrumbService.setNestedDynamicBreadcrumbs(
                WORK_QUEUE_URL,
                userId,
                data.businessName,
                undefined,
                'View Task'
              );
            } else if (data.firstName && data.lastName) {
              const userName = `${data.firstName} ${data.lastName}`;
              this.breadcrumbService.setNestedDynamicBreadcrumbs(WORK_QUEUE_URL, userId, userName, undefined, 'View Task');
            }
          }
        },
        error: (error: unknown) => this.appInsightsService.log(error instanceof Error ? error : new Error(String(error)))
      });
    }
  }

  submitForm(success: boolean): void {
    if (success) {
      this.router.navigate([WORK_QUEUE_URL, this.userId]);
    }
    this.formSubmit.emit(success);
  }
}

